/**
 * Revision binding for build-stamped source locations.
 *
 * The sandbox dev-preview build stamps rendered elements with source
 * coordinates (`data-wf-loc-text`, see `babel-plugin-source-loc.mjs` in the sandbox
 * template). Those coordinates are only valid for the exact source revision
 * that produced the build — after an instant text apply or a code-editor save,
 * the DOM still carries the previous build's stamps until the rebuild lands.
 * To make staleness detectable, the babel plugin also stamps a content hash of
 * the file it transformed (`data-wf-src-rev`), and the Designer recomputes the
 * same hash over the projected source before trusting any coordinate.
 *
 * WHICH file that is matters, and getting it wrong is EXT-1735: this hashed a
 * globals-import prefix ahead of the source, which is the layout of the
 * PUBLISHED bundle (`addBrowserApiShimsToComponentFiles`, server-side only).
 * The stamping build never sees that file. Stamps exist only in the dev-preview
 * sandbox, whose projection (`projectComponentsToFiles`, via
 * `RemoteSandboxStore`) writes `source.code` verbatim — no prefix, and no
 * `globals.js` on the sandbox filesystem at all. The extra prefix made every
 * in-sync component fail the comparison, so `stampTrusted` was false on 100% of
 * production applies and the three stamped tiers never ran.
 *
 * The plugin ships baked into the sandbox image and cannot import from the
 * monorepo, so it carries its own copy of this hash. The two implementations
 * are pinned to each other end-to-end — over the real projection, not a
 * restatement of this file's own arithmetic — by the cross-boundary test in
 * `packages/domains/code-libraries/templates/__tests__/sandbox/base/babel-plugin-source-loc.test.ts`.
 * Change them in lockstep.
 */

/** FNV-1a 32-bit over UTF-16 code units, rendered in base 36. */
export function fnv1aHash(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

/**
 * The revision hash the babel plugin stamps for a component whose stored
 * source is `sourceCode`. The plugin hashes the file it sees on the sandbox
 * FS, and the sandbox projection writes the stored source unchanged, so the
 * stored source IS the hashed text.
 */
export function hashProjectedSourceForStamp(sourceCode: string): string {
  return fnv1aHash(sourceCode);
}

/**
 * Whether a build's stamped revision belongs to an older source than the one
 * stored now — i.e. the build raced an edit or an undo. Shared so the reducer
 * that drops the stale metadata and the telemetry that reports the drop can
 * never disagree about what "stale" means.
 *
 * An absent revision is never stale: builds produced before revision stamping
 * (or with it disabled) carry no revision to compare.
 */
export function isStaleSourceRevision(
  sourceRevision: string | undefined,
  storedSource: string | undefined
): boolean {
  return (
    sourceRevision !== undefined &&
    sourceRevision !== hashProjectedSourceForStamp(storedSource ?? '')
  );
}
